import _ from 'underscore';
import React from 'react';
import { browserHistory } from 'react-router';
import { connect } from 'react-redux';
import { routerActions } from 'react-router-redux';
import classNames from 'classnames';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import FeedUserTypes from 'common/feed-user-type';
import ProfileHeader from '../shared/profile-header';
import FollowersDialog from '../shared/followers-dialog/followers-dialog';
import SharingDialog from '../shared/sharing-dialog/sharing-dialog';
import ProfileBanner from 'components/shared/profile-banner';
import {
  closeDialog,
  openDialog,
  fetchProfileFollowers,
  followFeedUser,
  unfollowFeedUser,
} from 'common/actions';
import CustomizeProp from 'common/prop-types/customize';
import * as DialogTypes from 'common/dialog-types';
import AdTrackingUtil from 'utility/ad-tracking-utility';
import CommunityContentContainers from 'common/container-type';
import CommunityContentContainerTypes from 'common/container-subtype';
import CommunityContentSections from 'common/container-section';
import CommunityContentPlayers from 'common/container-player';
import DisplayAd from 'components/shared/display-ad';
import GoogleAdRefresher from '../../utility/google-ad-refresher';
import ads from '../../utility/ads';
import DisplayAdModel from '../../utility/ads/display-ad-model';
import { MediaSnowplowManager } from '../../utility/page-tracking-utility';
import browserDetect from '../../utility/browser-detect';
import { isOrWasAnAthlete } from '../../common/is-or-was-an-athlete';

class App extends React.Component {
  static propTypes = {
    customize: CustomizeProp.isRequired,
    forcePublicView: React.PropTypes.bool.isRequired,
    canDismissFeedItems: React.PropTypes.bool,
    presetFilterType: React.PropTypes.number,
    onlyVideos: React.PropTypes.bool.isRequired,
    onlyHighlights: React.PropTypes.bool.isRequired,
    highlightsAndVideos: React.PropTypes.bool.isRequired,
    isMobile: React.PropTypes.bool.isRequired,
    author: React.PropTypes.shape({
      primaryName: React.PropTypes.string.isRequired,
      videoViewCount: React.PropTypes.number.isRequired,
      followCount: React.PropTypes.number.isRequired,
      hudlAccountId: React.PropTypes.string.isRequired,
      primaryColor: React.PropTypes.string,
      profileLogoUri: React.PropTypes.string,
      mobileProfileLogoUri: React.PropTypes.string,
      profileBannerUri: React.PropTypes.string.isRequired,
      mobileProfileBannerUri: React.PropTypes.string.isRequired,
      profileUrlBase: React.PropTypes.string.isRequired,
      secondaryColor: React.PropTypes.string,
    }).isRequired,
    currentDialog: React.PropTypes.shape({
      dialogType: React.PropTypes.oneOf(_.values(DialogTypes)),
      data: React.PropTypes.object,
    }),
    followers: React.PropTypes.shape({
      oldestDateTime: React.PropTypes.instanceOf(Date),
    }).isRequired,
    dispatch: React.PropTypes.func.isRequired,
    user: React.PropTypes.object,
    routing: React.PropTypes.shape({
      locationBeforeTransitions: React.PropTypes.shape({
        pathname: React.PropTypes.string.isRequired,
      }),
    }),
    subNavItems: React.PropTypes.arrayOf(React.PropTypes.shape({
      name: React.PropTypes.string.isRequired,
      url: React.PropTypes.string.isRequired,
    })),
    children: React.PropTypes.oneOfType([
      React.PropTypes.element,
      React.PropTypes.arrayOf(React.PropTypes.element),
    ]),
    mediaSnowplowTrackerContext: React.PropTypes.object,
  }

  componentDidMount() {
    // Load first batch of followers
    this.props.dispatch(fetchProfileFollowers());
    // TODO: Logo page view for Hudl Accounts
    // this.props.dispatch(pageView(this.props.user.userId, this.props.team.teamId));

    // Make sure the navbar is showing when changing between tabs.
    // If going back to the timeline, restore the last scroll location.
    browserHistory.listen(location => {
      const header = document.querySelector('.prof-header');
      if (header && header.getBoundingClientRect) {
        const scrollArea = document.querySelector('.parallax');
        const rect = header.getBoundingClientRect();
        const navPosition = rect.bottom + scrollArea.scrollTop;
        const tab = this.getCurrentTab(location);

        if (tab === 'timeline' && this.timelinePosition && this.timelinePosition > navPosition) {
          scrollArea.scrollTop = this.timelinePosition;
        } else if (rect.bottom < 0) {
          scrollArea.scrollTop = navPosition;
        }
      }
    });

    new GoogleAdRefresher([400, 700, 1000, 1055]); // eslint-disable-line no-new
    MediaSnowplowManager.initialize(this.props.mediaSnowplowTrackerContext)
    MediaSnowplowManager.trackPagePings();
    MediaSnowplowManager.trackPage('AuthorPage');
  }

  getDialog() {
    if (!this.props.currentDialog) {
      return undefined;
    }

    const dispatch = this.props.dispatch;

    switch (this.props.currentDialog.dialogType) {
    case DialogTypes.NONE:
      return undefined;
    case DialogTypes.FOLLOWERS_DIALOG:
      return (
        <FollowersDialog
          key="followers-dialog"
          title="Followers"
          followers={this.props.followers}
          addUserToModal={!!(this.props.user && this.props.user.isFollowing)}
          user={this.props.user}
          onClose={() => dispatch(closeDialog(DialogTypes.FOLLOWERS_DIALOG))}
          onScrollNearBottom={() => dispatch(fetchProfileFollowers(this.props.followers.oldestDateTime))}
          onFollowUnfollow={this.onFollowUnfollowFromFollowersDialog.bind(this)}
        />
      );
    case DialogTypes.SHARE_PROFILE_DIALOG:
      return (
        <SharingDialog
          key="share-profile-dialog"
          onClose={() => dispatch(closeDialog(DialogTypes.SHARE_PROFILE_DIALOG))}
          user={this.props.user}
          pageUrl={window.location.origin + this.props.author.profileUrlBase}
          twitterText={`Check out ${this.props.author.primaryName} on @Hudl`}
          title="Share This Page"
          usageLogData={{
            OwnerId: this.props.author.hudlAccountId,
            Type: 'Hudl',
          }}
        />
      );
    case DialogTypes.SHARE_VIDEO_DIALOG:
      const dialogData = this.props.currentDialog.data;
      let namePossessive = this.props.author.primaryName;
      if (namePossessive[-1] === 's') {
        namePossessive += '\'';
      } else {
        namePossessive += '\'s';
      }

      return (
        <SharingDialog
          key="share-video-dialog"
          onClose={() => dispatch(closeDialog(DialogTypes.SHARE_VIDEO_DIALOG))}
          user={this.props.user}
          pageUrl={window.location.origin + dialogData.url}
          twitterText={dialogData.tweetText || `Check out ${namePossessive} ${dialogData.title} on @Hudl`}
          title="Share This Video"
          usageLogData={{
            OwnerId: this.props.author.hudlAccountId,
            UserId: this.props.user && this.props.user.userId,
            OwnerType: 'Hudl',
            Type: dialogData.type,
            Origin: 'AuthorProfile',
            [(dialogData.type === 'VideoContent') ? 'ContentId' : 'ReelId']: dialogData.id,
          }}
        />
      );
    default:
      return undefined;
    }
  }

  onFollowUnfollow(feedUserTypeId, feedUserRelatedId, isFollow, contentFavoriteAthleteLocation = null) {
    const isCurrentProfile = feedUserTypeId === FeedUserTypes.Hudl
      && feedUserRelatedId === this.props.author.hudlAccountId;
    const logData = {
      adTrackingId: AdTrackingUtil.getAdTrackingIdAndSetCookie(),
      container: CommunityContentContainers.Profile,
      containerType: CommunityContentContainerTypes.Author,
      containerSection: CommunityContentSections.Timeline,
      player: CommunityContentPlayers.Unknown,
    };

    if (isFollow) {
      this.props.dispatch(
        followFeedUser(feedUserTypeId, feedUserRelatedId, isCurrentProfile, logData)
      );
    } else {
      this.props.dispatch(
        unfollowFeedUser(feedUserTypeId, feedUserRelatedId, isCurrentProfile, logData)
      );
    }
    // Only track favorites for active athletes
    isOrWasAnAthlete(feedUserRelatedId, feedUserTypeId).then((isOrWasAnAthlete) => {
      if (isOrWasAnAthlete) {
        MediaSnowplowManager.trackContentFavoriteEventForAthlete(
          isFollow ? 'Add' : 'Remove',
          contentFavoriteAthleteLocation,
          { userId: feedUserRelatedId },
          'Author'
        );
      }
    });
  }


  onFollowUnfollowFromFollowersDialog(feedUserTypeId, feedUserRelatedId, isFollow) {
    this.onFollowUnfollow(feedUserTypeId, feedUserRelatedId, isFollow, 'Followers Dialog');
  }

  getNavItems() {
    const navItems = [
      {
        key: 'profiles.tab_timeline',
        name: 'timeline',
      },
    ];

    if (this.props.onlyVideos || this.props.highlightsAndVideos) {
      navItems.push(
        {
          key: 'profiles.tab_videos',
          name: 'videos',
        }
      );
    }

    if (this.props.onlyHighlights) {
      navItems.push(
        {
          key: 'profiles.tab_highlights',
          name: 'highlights',
        }
      );
    }

    return navItems;
  }

  getCurrentTab(location = this.props.routing.locationBeforeTransitions) {
    const path = location ? location.pathname : 'timeline';
    const indexOfLastSlash = path.lastIndexOf('/');
    const result = path.substring(indexOfLastSlash + 1);
    const navNames = _.map(this.getNavItems(), navItem => navItem.name);
    if (!navNames.some(s => s === result)) {
      return 'timeline';
    }
    return result;
  }

  onChangeTab(name, changeWindowLocation) {
    let tab = name;
    if (this.props.onlyHighlights && tab === 'videos') {
      tab = 'highlights';
    }

    const dispatch = this.props.dispatch;
    let newRoute;
    newRoute = `${this.props.author.profileUrlBase}/${tab}`;
    newRoute += document.location.search + document.location.hash;
    if (changeWindowLocation) {
      window.location = window.location.origin + newRoute;
    } else {
      const scrollArea = document.querySelector('.parallax');
      const currentTab = this.getCurrentTab();
      if (currentTab === 'timeline') {
        this.timelinePosition = scrollArea.scrollTop;
      }

      dispatch(routerActions.push(newRoute));
    }
  }

  onFollowersCountClick(e) {
    e.preventDefault();
    this.props.dispatch(openDialog(DialogTypes.FOLLOWERS_DIALOG));
    return false;
  }

  renderMobileAd() {

    const currentTab = this.getCurrentTab();
    const adModel = {
      'highlights': new DisplayAdModel('div-gpt-ad-1467400049491-0', 'Display_HudlAccountAllVideos_Mobile'),
      'videos': new DisplayAdModel('div-gpt-ad-1467400049491-0', 'Display_HudlAccountAllVideos_Mobile'),
      'timeline': new DisplayAdModel('div-gpt-ad-1467400309126-0', 'Display_HudlAccountTimeline_Mobile'),
    }[currentTab];

    return (
      <DisplayAd
        id={adModel.getId()}
        isMobile
        sizes={[ads.sizes.MobileLeaderboard]}
        path={adModel.getPath()}
        adSizeMapping={[ads.mappings.mobileLeaderboard, ads.mappings.leaderboard]}
      />
    );
  }

  render() {
    const isMobile = this.props.isMobile || browserDetect.isModernIPad;
    const isLoggedIn = !!(this.props.user && this.props.user.userId);
    const onChangeTab = this.onChangeTab.bind(this);
    const classes = classNames({
      'loggedin': isLoggedIn,
      'mobile': isMobile,
      'prof-flex-height': true,
    });

    return (
      <div className={classes}>
        <div className="parallax-wrapper">
          <div className="parallax">
            <ProfileBanner
              customize={this.props.customize}
              isLoggedIn={isLoggedIn}
              isMobile={isMobile}
              isPrivilegedUser={this.props.user && this.props.user.isPrivilegedUser}
              feedUserType={FeedUserTypes.Hudl}
              forcePublicView={this.props.forcePublicView}
              onChangeBannerImage={() => {}}
              onErrorPicture={() => {}}
              profileBannerUri={this.props.author.profileBannerUri}
              mobileProfileBannerUri={this.props.author.mobileProfileBannerUri}
            />
            <ProfileHeader
              forcePublicView={this.props.forcePublicView}
              primaryName={this.props.author.primaryName}
              tagline=""
              customize={this.props.customize}
              highlightViewCount={this.props.author.videoViewCount}
              followCount={this.props.author.followCount}
              feedUserType={FeedUserTypes.Hudl}
              feedUserId={this.props.author.hudlAccountId}
              isMobile={isMobile}
              profileColor={this.props.author.primaryColor}
              profileLogoUri={this.props.author.profileLogoUri}
              mobileProfileLogoUri={this.props.author.mobileProfileLogoUri}
              secondaryColor={this.props.author.secondaryColor}
              user={this.props.user}
              onFollowersCountClick={(e) => this.onFollowersCountClick(e)}
              onShareButtonClick={() => this.props.dispatch(openDialog(DialogTypes.SHARE_PROFILE_DIALOG))}
              onPublicViewButtonClick={() => {}}
              onEditProfileClick={() => {}}
              onFollowUnfollow={this.onFollowUnfollow.bind(this)}
              isPrivilegedUser={this.props.user && this.props.user.isPrivilegedUser}
              currentTab={this.getCurrentTab()}
              onChangeTab={onChangeTab}
              onChangeProfileImage={() => {}}
              navItems={this.getNavItems()}
              onlyHighlights={this.props.onlyHighlights}
            >
              {React.cloneElement(this.props.children, Object.assign({ onChangeTab: onChangeTab }, this.props))}
            </ProfileHeader>
          </div>
        </div>
        <ReactCSSTransitionGroup transitionName="react-modal" transitionEnterTimeout={500}
          transitionLeaveTimeout={300}>
          {this.getDialog()}
        </ReactCSSTransitionGroup>
        {isMobile && this.renderMobileAd()}
      </div>
    );
  }
}

function select(state) {
  return state;
}

export default connect(select)(App);
